/**
 * Theme preference: light, dark, or system.
 *
 * Two rules from SUAP-0102 drive the whole file:
 *
 * 1. **Light is the default.** Not "light unless the OS says otherwise" — light.
 *    `system` is a value the user picks, never one they land on by omission.
 * 2. **Nothing here may throw.** `localStorage` is not merely null-returning in
 *    a locked-down or private context, it raises on access. A theme is not
 *    worth a white screen, so every read is defended and every failure resolves
 *    to light.
 *
 * The stylesheet does the rest: `[data-theme='dark']` re-points the token
 * values, so nothing outside this file ever asks which theme is active.
 */

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'suap.theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'

/** The fallback for every failure path: unreadable, absent, or malformed. */
export const DEFAULT_PREFERENCE: ThemePreference = 'light'

function isPreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system'
}

/**
 * The stored preference, or `light`.
 *
 * A value that is not one of the three literals is treated as absent rather
 * than repaired — a stale or hand-edited key should not decide the theme.
 */
export function readPreference(): ThemePreference {
  try {
    const stored: unknown = window.localStorage.getItem(STORAGE_KEY)
    return isPreference(stored) ? stored : DEFAULT_PREFERENCE
  } catch {
    return DEFAULT_PREFERENCE
  }
}

/** Persist a preference. A storage failure is not fatal: the theme still applies for this session. */
export function writePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, preference)
  } catch {
    // Private mode, disabled storage, quota. The session keeps the theme; the
    // next load falls back to light, which is the documented default anyway.
  }
}

function prefersDark(): boolean {
  try {
    return window.matchMedia(DARK_QUERY).matches
  } catch {
    return false
  }
}

/** `system` asks the OS; light and dark are already resolved. */
export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') return prefersDark() ? 'dark' : 'light'
  return preference
}

/**
 * Stamp the resolved theme onto the document.
 *
 * `data-theme` is always written, dark and light alike, so the attribute is the
 * single answer to "what is showing" — a missing attribute would mean light by
 * accident rather than by decision.
 */
export function applyTheme(theme: ResolvedTheme): void {
  const root = document.documentElement
  root.dataset['theme'] = theme
  root.style.colorScheme = theme
}

/** Choose a preference: persist it and apply it in one call. */
export function setPreference(preference: ThemePreference): void {
  writePreference(preference)
  applyTheme(resolveTheme(preference))
}

/**
 * Apply the stored preference and keep it live.
 *
 * Called from main.tsx *before* the first render, so the first paint is already
 * the right theme — a useEffect here would paint light and then flip.
 *
 * The OS listener is attached only while the preference is `system`; on any
 * other preference an OS change is not ours to react to. Returns the teardown
 * so a settings screen can re-init after a change.
 */
export function initTheme(): () => void {
  const preference = readPreference()
  applyTheme(resolveTheme(preference))

  if (preference !== 'system') return () => undefined

  try {
    const query = window.matchMedia(DARK_QUERY)
    const onChange = (event: MediaQueryListEvent): void => {
      applyTheme(event.matches ? 'dark' : 'light')
    }
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  } catch {
    return () => undefined
  }
}
